import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Check, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PASSWORD_RULES = [
  { rule: /.{10,}/, label: 'At least 10 characters' },
  { rule: /[a-z]/, label: 'Lowercase letter' },
  { rule: /[A-Z]/, label: 'Uppercase letter' },
  { rule: /\d/, label: 'Digit' },
  { rule: /[!@#$%^&*()\-_=+\[\]{};:,.<>?/|~`]/, label: 'Special character' },
];

const InviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Invitation not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const ruleStatus = PASSWORD_RULES.map((r) => ({ ...r, ok: r.rule.test(newPassword) }));
  const allPassed = ruleStatus.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (!allPassed) { toast.error('Password does not meet policy'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/auth/invite/${token}/accept`, { new_password: newPassword });
      toast.success('Account activated! You can now log in.');
      navigate('/login');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to activate');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-orange-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Activate your Suubi account</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : error ? (
            <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="bg-muted/50 p-3 rounded text-sm">
                <div><span className="text-muted-foreground">Name:</span> <strong>{invite.full_name}</strong></div>
                <div><span className="text-muted-foreground">Email:</span> {invite.email}</div>
                <div><span className="text-muted-foreground">Role:</span> {invite.role}</div>
              </div>
              <div>
                <Label>Set your password</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="invite-new-pw" autoComplete="new-password" />
              </div>
              <div>
                <Label>Confirm password</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="invite-confirm-pw" autoComplete="new-password" />
              </div>
              <ul className="text-xs space-y-1 bg-muted/50 p-3 rounded">
                {ruleStatus.map((r) => (
                  <li key={r.label} className={`flex items-center gap-2 ${r.ok ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'}`}>
                    <Check className={`h-3 w-3 ${r.ok ? 'opacity-100' : 'opacity-30'}`} />
                    {r.label}
                  </li>
                ))}
              </ul>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="invite-submit-btn">
                {submitting ? 'Activating…' : 'Activate account'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InviteAcceptPage;
